import { createAsyncThunk, createSlice, type PayloadAction } from '@reduxjs/toolkit';
import cloneDeep from 'lodash/cloneDeep';
import { DEFAULT_TABLE_PAGE_SIZE } from 'src/constants/Pagination';
import TaskService from 'src/services/tasks/taskService';
import { type PickOne } from 'src/types/utilities';
import { arePrimitiveValuesDiff } from 'src/utils/helpers';
import { createTags, extractAvailableFields, filterByBooleans, filterByStrings } from 'src/utils/tableUtils';

import type { FilterTag, RegionForm, RegionFormTableRow, RestrictionFormTableFilter } from './types';

// **** Functions **** //

/**
 * Convert region object to a table row
 */
const regionFormToFormDataRow = (region: RegionForm): RegionFormTableRow => ({
  id: region.id,
  name: region.name,
  country: region.country?.name,
  formName: region.restrictionFormName == undefined ? `${region.name} Form` : region.restrictionFormName,
  isActive: region.restrictionFormActive == null ? false : region.restrictionFormActive,
  description: region.restrictionFormDescription,
});

/**
 * Setup the Region/Task Filter Options from the table data.
 */
const setupRestrictionFormTableFilterOptions = (tableData: RegionFormTableRow[]): RestrictionFormTableFilter => ({
  stateRegions: extractAvailableFields(tableData, 'name'),
  applicableCountries: extractAvailableFields(tableData, 'country'),
  formNames: extractAvailableFields(tableData, 'formName'),
  isActiveSelections: ['Yes', 'No'],
});

/**
 * Setup filtered table data using the JobConfig Table filter.
 */
const applyRestrictionFormTableFilterUtil = (
  tableData: RegionFormTableRow[],
  filter: RestrictionFormTableFilter,
): RegionFormTableRow[] => {
  let filteredData = cloneDeep(tableData);
  filteredData = filterByStrings(filteredData, 'name', filter.stateRegions);
  filteredData = filterByStrings(filteredData, 'country', filter.applicableCountries);
  filteredData = filterByStrings(filteredData, 'formName', filter.formNames);
  filteredData = filterByBooleans(
    filteredData,
    'isActive',
    filter.isActiveSelections.map((isActive) => isActive === 'Yes'),
  );
  return filteredData;
};

/**
 * Get the applied filter tabl from the JobConfig Table filter.
 */
const getRestrictionFormTableFilterTags = (filter: RestrictionFormTableFilter): FilterTag[] => [
  ...createTags(filter, 'stateRegions', 'State/Region/Province'),
  ...createTags(filter, 'applicableCountries', 'Country'),
  ...createTags(filter, 'formNames', 'Form Name'),
  ...createTags(filter, 'isActiveSelections', 'Is Active'),
];

/**
 * Check if there is any filter data.
 */
export const getHasRestrictionFormTableFilters = (filter: RestrictionFormTableFilter): boolean =>
  Object.values(filter).some((arr) => arr.length > 0);

// **** Setup Async-Thunks **** //

/**
 * Fetch regions
 */
export const fetchRegions = createAsyncThunk('regionTasks/fetchRegions', () => {
  return TaskService.fetchRegions();
});

// **** Setup Redux-Slice **** //

const initialState: RegionTaskPageState = {
  regionTableData: [],
  filteredRegionTableData: [],
  isFetchingRegions: false,
  currentPage: 1,
  totalPages: 0,
  perPage: DEFAULT_TABLE_PAGE_SIZE,
  searchField: '',
  regions: [],
  selectedRegion: {
    id: 0,
    name: '',
    country: '',
    formName: '',
    isActive: false,
    description: '',
  },
  isRestrictionFormTableFilterOpen: false,
  restrictionFormTableFilterOptions: {
    stateRegions: [],
    applicableCountries: [],
    formNames: [],
    isActiveSelections: [],
  },
  restrictionFormTableFilter: {
    stateRegions: [],
    applicableCountries: [],
    formNames: [],
    isActiveSelections: [],
  },
  restrictionFormTableFilterTags: [],
};

interface RegionTaskPageState {
  regionTableData: RegionFormTableRow[];
  filteredRegionTableData: RegionFormTableRow[];
  isFetchingRegions: boolean;
  currentPage: number;
  totalPages: number;
  perPage: number;
  searchField: string;
  regions: RegionForm[];
  selectedRegion: RegionFormTableRow;
  isRestrictionFormTableFilterOpen: boolean;
  restrictionFormTableFilterOptions: RestrictionFormTableFilter;
  restrictionFormTableFilter: RestrictionFormTableFilter;
  restrictionFormTableFilterTags: FilterTag[];
}

const { reducer, actions } = createSlice({
  name: 'regionTasksConfigPageSlice',
  initialState,
  reducers: {
    setSearchField: (state, action) => {
      state.searchField = action.payload;
    },
    setCurrentPage: (state, action) => {
      state.currentPage = action.payload;
    },
    setPerPage: (state, action) => {
      state.perPage = action.payload;
    },
    setTotalPages: (state, action) => {
      state.totalPages = action.payload;
    },
    setSelectedRegion: (state, action) => {
      state.selectedRegion = action.payload;
    },
    resetSeletedRegion: (state) => {
      state.selectedRegion = { ...initialState.selectedRegion };
    },
    setIsRestrictionFormTableFilterSheetOpen: (state, action) => {
      state.isRestrictionFormTableFilterOpen = action.payload;
    },
    setRestrictionFormTableFilter: (state, action) => {
      state.restrictionFormTableFilter = action.payload;
    },
    setRestrictionFormTableFilterProp: (state, action: PayloadAction<PickOne<RestrictionFormTableFilter>>) => {
      state.restrictionFormTableFilter = {
        ...state.restrictionFormTableFilter,
        ...action.payload,
      };
    },
    applyRestrictionFormTableFilter: (state) => {
      state.filteredRegionTableData = applyRestrictionFormTableFilterUtil(
        state.regionTableData,
        state.restrictionFormTableFilter,
      );
      state.restrictionFormTableFilterTags = getRestrictionFormTableFilterTags(state.restrictionFormTableFilter);
    },
    resetRestrictionFormTableFilter: (state) => {
      state.restrictionFormTableFilter = {
        stateRegions: [],
        applicableCountries: [],
        formNames: [],
        isActiveSelections: [],
      };
    },
    removeTagAndUpdateFilter: (state, action) => {
      const newFilter = { ...state.restrictionFormTableFilter };
      const filterTag: FilterTag = action.payload;
      const { optionName, value } = filterTag;
      newFilter[optionName] = newFilter[optionName].filter((item) => arePrimitiveValuesDiff(item, value));
      state.restrictionFormTableFilter = newFilter;
      state.restrictionFormTableFilterTags = getRestrictionFormTableFilterTags(newFilter);
      state.filteredRegionTableData = applyRestrictionFormTableFilterUtil(state.regionTableData, newFilter);
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchRegions.pending, (state) => {
      state.isFetchingRegions = true;
    });
    builder.addCase(fetchRegions.fulfilled, (state, { payload }) => {
      state.regions = payload;
      const data = payload.map((region: any) => regionFormToFormDataRow(region));
      state.regionTableData = data;
      state.restrictionFormTableFilterOptions = setupRestrictionFormTableFilterOptions(data);
      state.isFetchingRegions = false;
      if (getHasRestrictionFormTableFilters(state.restrictionFormTableFilter)) {
        state.filteredRegionTableData = applyRestrictionFormTableFilterUtil(data, state.restrictionFormTableFilter);
        state.restrictionFormTableFilterTags = getRestrictionFormTableFilterTags(state.restrictionFormTableFilter);
      } else {
        state.filteredRegionTableData = [...data];
      }
    });
    builder.addCase(fetchRegions.rejected, (state) => {
      state.isFetchingRegions = false;
    });
  },
});

// **** Export **** //

export const {
  setSearchField,
  setCurrentPage,
  setPerPage,
  setTotalPages,
  setSelectedRegion,
  resetSeletedRegion,
  setIsRestrictionFormTableFilterSheetOpen,
  setRestrictionFormTableFilter,
  setRestrictionFormTableFilterProp,
  applyRestrictionFormTableFilter,
  resetRestrictionFormTableFilter,
  removeTagAndUpdateFilter,
} = actions;

export default reducer;
